import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Steps } from 'antd';
import { LockOutlined, MailOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { authService } from '../../services/authService';
import { validatePassword } from '../../utils/passwordValidator';
import { TestVersionNotice } from '../common/TestVersionNotice';
import { useI18n } from '../../i18n/useI18n';

import './AuthStyles.css';

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

const SEND_CODE_COOLDOWN_SEC = 60;

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBackToLogin }) => {
  const [form] = Form.useForm();
  const { t } = useI18n();
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const resetAlerts = () => {
    setError(null);
    setInfoMessage(null);
  };

  const sendVerificationCode = useCallback(async () => {
    resetAlerts();
    try {
      await form.validateFields(['email']);
      const values = form.getFieldsValue();
      setIsLoading(true);

      const message = await authService.sendForgotPasswordEmailCode(values.email);
      setInfoMessage(message);
      setStep(1);
      setCooldown(SEND_CODE_COOLDOWN_SEC);
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) {
        return;
      }
      setError(err instanceof Error ? err.message : t('auth.sendCodeFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [form, t]);

  const handleResetPassword = async () => {
    resetAlerts();
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      if (values.newPassword !== values.confirmPassword) {
        setError(t('auth.passwordsMismatch'));
        return;
      }

      setIsLoading(true);
      const message = await authService.resetPasswordByEmail({
        email: values.email,
        code: values.code,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });

      setSuccessMessage(message);
      setStep(2);
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) {
        return;
      }
      setError(err instanceof Error ? err.message : t('auth.resetError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card auth-card--wide">
        <TestVersionNotice variant="auth" />

        <div className="logo-section">
          <div className="hexagon-logo">
            <img src="/images/GH-logo.png" alt="GH Logo" />
          </div>
          <h1 className="brand-text">{t('auth.recoverPassword')}</h1>
          <p className="auth-subtitle">{t('auth.recoverSubtitle')}</p>
        </div>

        <Steps
          className="auth-recovery-steps"
          size="small"
          current={step}
          items={[
            { title: t('auth.verifyAccount') },
            { title: t('auth.codeAndNewPassword') },
            { title: t('auth.complete') },
          ]}
        />

        {error && (
          <div className="error-message">
            <Alert message={t('auth.operationFailed')} description={error} type="error" showIcon closable onClose={() => setError(null)} />
          </div>
        )}

        {infoMessage && step === 1 && (
          <div className="success-message">
            <Alert message={t('auth.codeSent')} description={infoMessage} type="info" showIcon closable onClose={() => setInfoMessage(null)} />
          </div>
        )}

        {successMessage && step === 2 && (
          <div className="success-message">
            <Alert message={t('auth.resetSuccess')} description={successMessage} type="success" showIcon />
          </div>
        )}

        {step < 2 ? (
          <Form form={form} layout="vertical" className="auth-form">
            <Form.Item
              name="email"
              rules={[
                { required: true, message: t('auth.emailRequired') },
                { type: 'email', message: t('auth.emailInvalid') },
              ]}
              className="form-item"
            >
              <Input
                prefix={<MailOutlined className="input-icon" />}
                placeholder={t('auth.registeredEmail')}
                className="auth-input"
                size="large"
                autoComplete="email"
              />
            </Form.Item>

            {step === 1 && (
              <>
                <Form.Item
                  name="code"
                  rules={[
                    { required: true, message: t('auth.codeRequired') },
                    { pattern: /^\d{6}$/, message: t('auth.codeInvalid') },
                  ]}
                  className="form-item"
                >
                  <Input
                    prefix={<SafetyCertificateOutlined className="input-icon" />}
                    placeholder={t('auth.codePlaceholder')}
                    className="auth-input"
                    size="large"
                    inputMode="numeric"
                    maxLength={6}
                  />
                </Form.Item>

                <Form.Item
                  name="newPassword"
                  rules={[
                    { required: true, message: t('auth.newPasswordRequired') },
                    { min: 8, message: t('auth.passwordMinLength') },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const validation = validatePassword(value);
                        return validation.valid
                          ? Promise.resolve()
                          : Promise.reject(new Error(validation.message));
                      },
                    },
                  ]}
                  className="form-item"
                >
                  <Input.Password
                    prefix={<LockOutlined className="input-icon" />}
                    placeholder={t('auth.newPasswordPlaceholder')}
                    className="auth-input"
                    size="large"
                    autoComplete="new-password"
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  dependencies={['newPassword']}
                  rules={[
                    { required: true, message: t('auth.confirmPasswordRequired') },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t('auth.passwordsMismatch')));
                      },
                    }),
                  ]}
                  className="form-item"
                >
                  <Input.Password
                    prefix={<LockOutlined className="input-icon" />}
                    placeholder={t('auth.confirmPasswordPlaceholder')}
                    className="auth-input"
                    size="large"
                    autoComplete="new-password"
                  />
                </Form.Item>
              </>
            )}

            <div className="auth-form-actions">
              {step === 0 ? (
                <Button
                  type="primary"
                  className="auth-button"
                  size="large"
                  loading={isLoading}
                  disabled={cooldown > 0}
                  onClick={() => void sendVerificationCode()}
                >
                  {cooldown > 0
                    ? t('auth.resendShortAfter', { seconds: cooldown })
                    : t('auth.sendCode')}
                </Button>
              ) : (
                <>
                  <Button
                    className="auth-button auth-button--secondary"
                    size="large"
                    disabled={isLoading}
                    onClick={() => {
                      setStep(0);
                      resetAlerts();
                    }}
                  >
                    {t('auth.previous')}
                  </Button>
                  <Button
                    type="primary"
                    className="auth-button"
                    size="large"
                    loading={isLoading}
                    onClick={() => void handleResetPassword()}
                  >
                    {t('auth.resetPassword')}
                  </Button>
                  <Button
                    type="link"
                    className="auth-link-button"
                    disabled={isLoading || cooldown > 0}
                    onClick={() => void sendVerificationCode()}
                  >
                    {cooldown > 0
                      ? t('auth.resendCodeAfter', { seconds: cooldown })
                      : t('auth.resendCode')}
                  </Button>
                </>
              )}
            </div>
          </Form>
        ) : (
          <div className="auth-form-actions">
            <Button type="primary" className="auth-button" size="large" onClick={onBackToLogin}>
              {t('auth.backToLogin')}
            </Button>
          </div>
        )}

        {step < 2 && (
          <div className="auth-links">
            <button type="button" className="link-button" onClick={onBackToLogin}>
              {t('auth.backToLogin')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
